import { FC, useEffect, useMemo, useState } from "react";
import { MemberEditUser } from "../../../types/api/admin/user";
import ModalForm from "../../../components/ModalForm";
import { toast } from "react-toastify";
import { updateUserById } from "../../../services/api/admin/user";
import { Role } from "../../../types/enums/Role";
import { getFormattedDateInput } from "../../../utils/date";
import {
    FieldErrors,
    REQUIRED_FIELD,
    clearFieldError,
    notifyFieldErrors,
    setFieldErrors,
} from "../../../utils/errors";
import { isStaffRole } from "../../../utils/graduation";

type Props = {
    open: boolean;
    handleClose: () => void;
    user?: MemberEditUser;
    roles: { id: string; name: string }[];
    onUserUpdated?: () => void;
};

const ROLE_SINCE_EDITABLE = new Set<string>([
    Role.Junior,
    Role.Expert,
    Role.Pro,
    Role.Independant,
    Role.Forme,
]);

const MemberModal: FC<Props> = ({
    open,
    handleClose,
    user,
    roles,
    onUserUpdated,
}) => {
    const [firstname, setFirstname] = useState("");
    const [lastname, setLastname] = useState("");
    const [email, setEmail] = useState("");
    const [roleId, setRoleId] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [roleSince, setRoleSince] = useState("");
    const [fieldErrors, setFieldErrorsState] = useState<FieldErrors>({});

    const selectedRoleName = useMemo(
        () => roles.find((role) => role.id === roleId)?.name ?? "",
        [roles, roleId],
    );
    const canEditRoleSince = ROLE_SINCE_EDITABLE.has(selectedRoleName);

    useEffect(() => {
        if (user) {
            setFirstname(user.firstname || "");
            setLastname(user.lastname || "");
            setEmail(user.email);
            setRoleId(user.roleId);
            setIsActive(user.isActive);
            setRoleSince(user.roleSince ?? "");
        }
    }, [user]);

    const getRolesOptions = () => {
        const traderRoles = roles.filter((role) => !isStaffRole(role.name));
        const current = roles.find((role) => role.id === roleId);
        // Si le membre est déjà staff, on conserve son rôle courant pour l'affichage
        // (la promotion staff se fait via « Promouvoir en staff »).
        if (
            current &&
            isStaffRole(current.name) &&
            !traderRoles.some((role) => role.id === current.id)
        ) {
            return [
                { value: current.id, label: current.name },
                ...traderRoles.map((role) => ({
                    value: role.id,
                    label: role.name,
                })),
            ];
        }
        return traderRoles.map((role) => ({
            value: role.id,
            label: role.name,
        }));
    };

    const handleRoleChange = (value: string) => {
        setRoleId(value);
        const nextRoleName = roles.find((role) => role.id === value)?.name ?? "";
        if (ROLE_SINCE_EDITABLE.has(nextRoleName)) {
            setRoleSince((current) => current || getFormattedDateInput(new Date()));
            return;
        }
        setRoleSince("");
    };

    const handleSubmit = async () => {
        const nextErrors = setFieldErrors(
            ["firstName", !firstname.trim() && REQUIRED_FIELD],
            ["lastName", !lastname.trim() && REQUIRED_FIELD],
            ["email", !email.trim() && REQUIRED_FIELD],
            ["roleId", !roleId && REQUIRED_FIELD],
            [
                "roleSince",
                canEditRoleSince &&
                    !roleSince &&
                    `Veuillez renseigner la date de passage en ${selectedRoleName}.`,
            ]
        );
        setFieldErrorsState(nextErrors);
        const errorKeys = Object.keys(nextErrors);
        const toastMessage =
            errorKeys.length === 1 && errorKeys[0] === "roleSince"
                ? nextErrors.roleSince
                : "Veuillez remplir tous les champs.";
        if (
            notifyFieldErrors(nextErrors, {
                message: toastMessage,
                type: "error",
            })
        ) {
            return;
        }

        try {
            await updateUserById(user!.id, {
                firstname: firstname.trim(),
                lastname: lastname.trim(),
                email: email.trim(),
                roleId,
                isActive,
                proSince: canEditRoleSince ? roleSince : null,
            });
            onUserUpdated?.();
            handleClose();
            toast.success("Utilisateur modifié avec succès !");
        } catch {
            toast.error(
                "Impossible de modifier le membre. Vérifiez les champs ou réessayez.",
            );
        }
    };

    return (
        <ModalForm
            open={open}
            handleClose={handleClose}
            fields={[
                {
                    id: "firstName",
                    label: "Prénom",
                    type: "text",
                    value: firstname,
                    onChange: (value) => {
                        setFirstname(value as string);
                        setFieldErrorsState((prev) =>
                            clearFieldError(prev, "firstName")
                        );
                    },
                    error: fieldErrors.firstName,
                },
                {
                    id: "lastName",
                    label: "Nom",
                    type: "text",
                    value: lastname,
                    onChange: (value) => {
                        setLastname(value as string);
                        setFieldErrorsState((prev) =>
                            clearFieldError(prev, "lastName")
                        );
                    },
                    error: fieldErrors.lastName,
                },
                {
                    id: "email",
                    label: "Email",
                    type: "text",
                    value: email,
                    onChange: (value) => {
                        setEmail(value as string);
                        setFieldErrorsState((prev) =>
                            clearFieldError(prev, "email")
                        );
                    },
                    error: fieldErrors.email,
                },
                {
                    id: "roleId",
                    label: "Rôle",
                    type: "select",
                    value: roleId,
                    onChange: (value) => {
                        handleRoleChange(value as string);
                        setFieldErrorsState((prev) =>
                            clearFieldError(prev, "roleId")
                        );
                    },
                    options: getRolesOptions(),
                    error: fieldErrors.roleId,
                },
                ...(canEditRoleSince
                    ? [
                          {
                              id: "roleSince",
                              label: `Date de passage en ${selectedRoleName}`,
                              type: "oldDate" as const,
                              value: roleSince,
                              onChange: (value: string) => {
                                  setRoleSince(value);
                                  setFieldErrorsState((prev) =>
                                      clearFieldError(prev, "roleSince")
                                  );
                              },
                              error: fieldErrors.roleSince,
                          },
                      ]
                    : []),
                {
                    id: "isActive",
                    label: "Actif",
                    type: "select",
                    value: String(isActive),
                    onChange: (value) => setIsActive(value === "true"),
                    options: [
                        { value: "true", label: "Oui" },
                        { value: "false", label: "Non" },
                    ],
                },
            ]}
            onSubmit={handleSubmit}
            title="Modifier un membre"
            size="small"
        />
    );
};

export default MemberModal;
